import React, {useEffect} from 'react';
import {Link, Breadcrumbs, Popup, Flex, Icon, Card, Settings, Button, PageLock} from 'components';
import {config, api, modal, capitalize, partial, useNamedSelector, useToggle, openPopup, useSelector} from 'helpers';
import {store, session} from 'store';
import css from './membership.module.scss';

export function Membership({ match: { params } }) {
  const [ lock, toggleLock ] = useToggle();
  const { subscription, plans, archived, current, isWebPremium } = useSelector(session.membership);

  useEffect(() => {
    if (params.upgraded) {
      modal(Settings.MembershipUpgraded)();
    }
  }, []);

  return (
    <div>
      <Flex className={['medium-spacing', 'middle', 'justified']}>
        <h3>{ subscription.premium ? 'Premium account lets you' : 'Upgrade to Premium account' }</h3>
        <Button icon="info" className={['tiny', 'borderless']} onClick={modal(Settings.MembershipMore)}>Learn more</Button>
      </Flex>
      <Settings.MembershipAlert title />

      <div className={css.features}>
        <Card>
          <Icon name="membership-0" />
          <p>Schedule Unlimited <br /> Routines</p>
        </Card>
        <Card>
          <Icon name="membership-1" />
          <p>Set Unlimited <br /> Goals</p>
        </Card>
        <Card>
          <Icon name="membership-2" />
          <p>Access <br /> '{capitalize(config.identityModule)}' Module</p>
        </Card>
        <Card>
          <Icon name="membership-3" />
          <p>Export & Share <br /> Your Plan </p>
        </Card>
        <Card>
          <Icon name="membership-4" />
          <p>Collaborate <br /> with a Team</p>
        </Card>
        <Card>
          <Icon name="membership-5" />
          <p>Full Premium <br /> Program Access</p>
        </Card>
      </div>
      <ul className={css.buttons}>
        { plans.map(plan =>
          <li key={plan.code}>
            <p className={css.tip}>{ plan.tip }</p>
            { plan.isCurrent
              ? <Button disabled className={['primary', 'stretch', 'filled']} icon="check">
                { plan.title.replace('1 Month', 'Monthly') }
              </Button>
              : <Button className={['stretch', 'orange', 'filled']} onClick={partial(purchase, { productCode: plan.code, toggleLock })}>
                { plan.title.replace('1 Month', 'Monthly') }
              </Button>
            }
            {(plan.isCurrent && subscription.renewalDate && subscription.renewalDate !== '') &&
              <p className={css.nextPaymentTip}>Next payment on { subscription.renewalDate }</p>
            }
          </li>
        ) }
      </ul>
      <p className="secondary small">
        By upgrading your account, you agree to the {' '}
        <a className="link" target="_blank" href={`https://${config.appHost}/terms/`}>Terms of Service</a>
        { ' and ' }
        <a className="link" target="_blank" href={`https://${config.appHost}/privacy/`}>Privacy Policy</a>.
      </p>
      { isWebPremium &&
        <Card className={['medium', 'medium-spacing']}>
          <Breadcrumbs>
            <Link to="/settings/">Settings</Link>
            <Link to="/settings/membership/">Membership</Link>
            <b>Current Plan</b>
          </Breadcrumbs>
          <Flex className={['middle', 'justified', 'spacing']}>
            <h2>
              { (current || archived).title.replace(/\s*\$[0-9.]+\s*USD/, '') }
              { subscription.premium && ' Premium' }
            </h2>
            <Button className="danger" onClick={openPopup(CancelSubscriptionMenu)}>Cancel</Button>
          </Flex>
        </Card>
      }
      { lock && <PageLock /> }
    </div>
  );
}

function CancelSubscriptionMenu() {
  const { user } = useNamedSelector('session.membership');
  return (
    <Popup.Menu>
      <Popup.Menu.Item onClick={modal(Settings.CancelSubscription, { user })}>Cancel subscription & <b>Keep</b> account</Popup.Menu.Item>
      <Popup.Menu.Item onClick={modal(Settings.CancelSubscription, { user, deleteAccount: true })}>Cancel subscription & <b>Delete</b> account</Popup.Menu.Item>
    </Popup.Menu>
  );
}

async function purchase({ productCode, toggleLock }) {
  toggleLock(true);
  try {
    const { url } = await api('/start-payment/', { productCode });
    store.dispatch({ type: 'SESSION_START_PAYMENT', productCode });
    if (/^http/.test(url)) {
      window.location = url;
    }
  } catch(error) {
    store.dispatch({
      type: 'UI_NOTIFICATION',
      errorMessage: 'Server is not responding. Please try again later.',
      error,
    });
  }
  toggleLock(false);
}
